import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../dtos/response-api';
import { Login } from '../../dtos/auth/login';
import { User } from '../../dtos/auth/user';
import { TokenToLoginSai } from '../../dtos/auth/tokenToLoginSai';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../http/http-context-tokens';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;
  private baseUrl = `${environment.apiUrl}/sai/core`;


  constructor(private http: HttpClient, private jwtHelper: JwtHelperService) {
    this.currentUserSubject = new BehaviorSubject<User | null>(
      this.buildUserFromToken(this.getToken())
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  loginJwt(usuario: string, senha: string): Observable<ResponseApi<Login>> {
    return this.http.post<ResponseApi<Login>>(
      `${this.baseUrl}/Account`,
      { usuario, senha },
      {
        context: new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true)
      }
    );
  }

  login(usuario: string, senha: string): Observable<User> {
    return this.loginJwt(usuario, senha).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error('Erro durante o login: ' + response.message);
        }

        const decodedToken = this.jwtHelper.decodeToken(response.data.token);
        const rolesArray = decodedToken['role'] || [];
        const roles = Array.isArray(rolesArray) ? rolesArray : [rolesArray];

        const user: User = {
          nome: decodedToken.Nome,
          email: decodedToken.Email,
          idUsuario: decodedToken.IdUsuario,
          idOrgao: decodedToken.IdOrgao,
          orgao: decodedToken.Orgao,
          idTipoOrgao: decodedToken.IdTipoOrgao,
          codLayoutPortal: decodedToken.CodLayoutPortal,
          idPerfil: decodedToken.IdPerfil,
          token: response.data.token,
          cnpjJuridico: decodedToken.CnpjJuridico,
          cnpjOrgao: decodedToken.CnpjOrgao,
          siteOrgao: decodedToken.SiteOrgao,
          roles: roles.map((role: string) => ({ name: role }))
        };

        localStorage.removeItem('token');
        localStorage.setItem('token', response.data.token);

        this.currentUserSubject.next(user);
        return user;
      })
    );
  }

  loginToken(token: string): Observable<ResponseApi<Login>> {
    return this.http.post<ResponseApi<Login>>(`${this.baseUrl}/Account/authSai`, { token });
  }

  loginSai(token: string): Observable<User> {
    return this.loginToken(token).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error('Erro durante o login: ' + response.message);
        }

        const decodedToken = this.jwtHelper.decodeToken(response.data.token);
        const rolesArray = decodedToken['role'] || [];
        const roles = Array.isArray(rolesArray) ? rolesArray : [rolesArray];

        const user: User = {
          nome: decodedToken.Nome,
          email: decodedToken.Email,
          idUsuario: decodedToken.IdUsuario,
          idOrgao: decodedToken.IdOrgao,
          orgao: decodedToken.Orgao,
          idTipoOrgao: decodedToken.IdTipoOrgao,
          codLayoutPortal: decodedToken.CodLayoutPortal,
          idPerfil: decodedToken.IdPerfil,
          cnpjJuridico: decodedToken.CnpjJuridico,
          cnpjOrgao: decodedToken.CnpjOrgao,
          siteOrgao: decodedToken.SiteOrgao,
          token: response.data.token,
          roles: roles.map((role: string) => ({ name: role }))
        };

        localStorage.removeItem('token');
        localStorage.setItem('token', response.data.token);

        this.currentUserSubject.next(user);
        return user;
      })
    );
  }

  changeOrgaoToken(codOrgaoOrg: number): Observable<ResponseApi<Login>> {
    return this.http.post<ResponseApi<Login>>(`${this.baseUrl}/Account/changeOrgao`, { codOrgaoOrg });
  }

  changeOrgao(codOrgaoOrg: number): Observable<User> {
    return this.changeOrgaoToken(codOrgaoOrg).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error('Erro durante o login: ' + response.message);
        }

        const decodedToken = this.jwtHelper.decodeToken(response.data.token);
        const rolesArray = decodedToken['role'] || [];
        const roles = Array.isArray(rolesArray) ? rolesArray : [rolesArray];

        const user: User = {
          nome: decodedToken.Nome,
          email: decodedToken.Email,
          idUsuario: decodedToken.IdUsuario,
          idOrgao: decodedToken.IdOrgao,
          orgao: decodedToken.Orgao,
          idTipoOrgao: decodedToken.IdTipoOrgao,
          codLayoutPortal: decodedToken.CodLayoutPortal,
          idPerfil: decodedToken.IdPerfil,
          cnpjJuridico: decodedToken.CnpjJuridico,
          cnpjOrgao: decodedToken.CnpjOrgao,
          siteOrgao: decodedToken.SiteOrgao,
          token: response.data.token,
          roles: roles.map((role: string) => ({ name: role }))
        };

        localStorage.removeItem('token');
        localStorage.setItem('token', response.data.token);

        this.currentUserSubject.next(user);
        return user;
      })
    );
  }

  logout() {
    localStorage.removeItem('token');
    this.currentUserSubject.next(null!);
    return of({ success: false });
  }

  getNome(): string | null {
    const decodeToken = this.getTokenDecoded();
    return decodeToken.Nome;
  }

  getEmail(): string | null {
    const decodeToken = this.getTokenDecoded();
    return decodeToken.Email;
  }

  getToken(): string | null {
    const userDataString = localStorage.getItem('token');
    if (userDataString) {
      return userDataString;
    }

    return null;
  }

  getTokenDecoded(): any {
    return this.jwtHelper.decodeToken(this.getToken()!);
  }

  private buildUserFromToken(token: string | null): User {
    if (!token) {
      return null!;
    }

    const decodedToken = this.jwtHelper.decodeToken(token);
    const rolesArray = decodedToken['role'] || [];
    const roles = Array.isArray(rolesArray) ? rolesArray : [rolesArray];

    return {
      nome: decodedToken.Nome,
      email: decodedToken.Email,
      idUsuario: decodedToken.IdUsuario,
      idOrgao: decodedToken.IdOrgao,
      orgao: decodedToken.Orgao,
      idTipoOrgao: decodedToken.IdTipoOrgao,
      codLayoutPortal: decodedToken.CodLayoutPortal,
      idPerfil: decodedToken.IdPerfil,
      cnpjJuridico: decodedToken.CnpjJuridico,
      cnpjOrgao: decodedToken.CnpjOrgao,
      siteOrgao: decodedToken.SiteOrgao,
      token: token,
      roles: roles.map((role: string) => ({ name: role }))
    };
  }

  generateTokenToLoginSai(path: string): Observable<ResponseApi<TokenToLoginSai>> {
    return this.http.post<ResponseApi<TokenToLoginSai>>(`${this.baseUrl}/Account/generateTokenToLoginSai`, { path });
  }

  isTokenExpired(): boolean {
    const token = this.getToken();

    if (!token) return true;

    return this.jwtHelper.isTokenExpired(token);
  }
}
